import { ElementCustomLayerProps } from "domains/canvas/components/LayerPanel";
import { maskImageId } from "domains/canvas/components/sections/AIPanel/maskImageId";
import { CanvasStore } from "domains/canvas/interfaces";

export const removeMasks = (store: CanvasStore) => {
  if (!store.activePage) return;

  store.deleteElements([maskImageId]);
};

export const hideMasks = (store: CanvasStore) => {
  if (!store.activePage) return;

  store.getElementById(maskImageId)?.set({ visible: false });
};

export const showMasks = (store: CanvasStore) => {
  if (!store.activePage) return;

  store.getElementById(maskImageId)?.set({ visible: true });
};

// create empty image png
export const emptyMask =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

export const maskColor = "#00fbff";

export const addMaskImage = async (store: CanvasStore) => {
  if (!store.activePage) return;

  if (store.getElementById(maskImageId)) return;

  await store.history.ignore(() => {
    store.activePage?.addElement({
      type: "image",
      x: 0,
      y: 0,
      width: store.width,
      height: store.height,
      id: maskImageId,
      name: "Mask",
      showInExport: false,
      keepRatio: false,
      draggable: false,
      resizable: false,
      styleEditable: false,
      removable: true,
      selectable: false,
      alwaysOnTop: true,
      visible: true,
      src: emptyMask,
      custom: {
        layers: {
          hidePosition: true,
          hideDuplicate: true,
          hideOpacity: true,
          hideVisible: true,
          hideDelete: false,
          hideLock: true,
          disableRename: true,
        } as ElementCustomLayerProps,
      },
    });
  });
};

export const maskClipPattern = `data:image/svg+xml;base64,${btoa(
  `<svg height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
  <circle cx="4" cy="4" r="4" />
</svg>`
)}`;

export const generateMask = async (
  originalImage: string,
  { width, height }: { width: number; height: number }
): Promise<string> => {
  const image = new Image();
  image.src = originalImage;

  return await new Promise((resolve, reject) => {
    image.onload = () => {
      // Create a Canvas element
      const canvas = document.createElement("canvas");

      // Set the size of the canvas to match the image
      canvas.width = width;
      canvas.height = height;

      // Draw the image on the canvas
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject("Failed to get canvas context");
        return;
      }

      ctx.drawImage(image, 0, 0);

      // Get the pixel data of the canvas
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixelData = imageData.data;

      // Loop through the pixel data and replace transparent pixels with white and non-transparent pixels with black
      for (let i = 0; i < pixelData.length; i += 4) {
        const alpha = pixelData[i + 3];
        if (alpha === 0) {
          pixelData[i] = 255;
          pixelData[i + 1] = 255;
          pixelData[i + 2] = 255;
          pixelData[i + 3] = 255;
        } else {
          pixelData[i] = 0;
          pixelData[i + 1] = 0;
          pixelData[i + 2] = 0;
          pixelData[i + 3] = 255;
        }
      }

      // Update the canvas with the new pixel data
      ctx.putImageData(imageData, 0, 0);

      // The canvas now contains the modified image
      resolve(canvas.toDataURL());
    };
  });
};

export const generateMaskInvert = async (
  originalImage: string,
  { width, height }: { width: number; height: number }
): Promise<string> => {
  const image = new Image();
  image.src = originalImage;

  return await new Promise((resolve, reject) => {
    image.onload = () => {
      // Create a Canvas element
      const canvas = document.createElement("canvas");

      // Set the size of the canvas to match the image
      canvas.width = width;
      canvas.height = height;

      // Draw the image on the canvas
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject("Failed to get canvas context");
        return;
      }

      ctx.drawImage(image, 0, 0);

      // Get the pixel data of the canvas
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixelData = imageData.data;

      // Loop through the pixel data and replace transparent pixels with white and non-transparent pixels with black
      for (let i = 0; i < pixelData.length; i += 4) {
        const alpha = pixelData[i + 3];
        if (alpha === 0) {
          pixelData[i] = 0;
          pixelData[i + 1] = 0;
          pixelData[i + 2] = 0;
          pixelData[i + 3] = 255;
        } else {
          pixelData[i] = 255;
          pixelData[i + 1] = 255;
          pixelData[i + 2] = 255;
          pixelData[i + 3] = 255;
        }
      }

      // Update the canvas with the new pixel data
      ctx.putImageData(imageData, 0, 0);

      // The canvas now contains the modified image
      resolve(canvas.toDataURL());
    };
  });
};
export const adjustMaskOpacities = async (
  mergedImage: string
): Promise<string> => {
  const image = new Image();
  image.src = mergedImage;

  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  ctx.drawImage(image, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixelData = imageData.data;

  for (let i = 0; i < pixelData.length; i += 4) {
    const alpha = pixelData[i + 3] / 255; // Normalize alpha to 0-1 range
    if (alpha > 0.5) {
      pixelData[i + 3] = 0.8 * 255; // Set alpha to 0.8
    } else if (alpha > 0) {
      pixelData[i + 3] = 0.01 * 255; // Set alpha to 0.01, so it shoes the mask pattern but is still almost fully transparent
    }
    // Keep alpha at 0
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL();
};

/** This function turns islands of white pixels into blocks of pixels */
export const turnIslandIntoRectangle = (mask: string): Promise<string> => {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = mask;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to get canvas context");

      // Draw image to canvas
      ctx.drawImage(image, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      // Clear canvas for drawing blocks
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "white";

      const BLOCK_SIZE = 16;

      // Scan image in blocks of BLOCK_SIZExBLOCK_SIZE pixels
      for (let blockY = 0; blockY < canvas.height; blockY += BLOCK_SIZE) {
        for (let blockX = 0; blockX < canvas.width; blockX += BLOCK_SIZE) {
          let hasWhitePixel = false;

          // Check each pixel in the block
          for (
            let y = blockY;
            y < Math.min(blockY + BLOCK_SIZE, canvas.height);
            y++
          ) {
            for (
              let x = blockX;
              x < Math.min(blockX + BLOCK_SIZE, canvas.width);
              x++
            ) {
              const i = (y * canvas.width + x) * 4;
              if (pixels[i] === 255) {
                // If white pixel found
                hasWhitePixel = true;
                break;
              }
            }
            if (hasWhitePixel) break;
          }

          // If block contains any white pixel, fill the entire block
          if (hasWhitePixel) {
            const width = Math.min(BLOCK_SIZE, canvas.width - blockX);
            const height = Math.min(BLOCK_SIZE, canvas.height - blockY);
            ctx.fillRect(blockX, blockY, width, height);
          }
        }
      }

      resolve(canvas.toDataURL());
    };
  });
};
